import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../utils/customToast";
import { useCompany } from "../../context/CompanyContext";
import { SECURITY_KEY } from "../../globals";
import { CompanySelectSkeleton } from "../Skeleton";

const SelectCompany = () => {
  const [companies, setCompanies] = useState(() => {
    try {
      const saved = localStorage.getItem("companyList");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [noCompany, setNoCompany] = useState(false);

  const navigate = useNavigate();
  const { setSelectedCompany } = useCompany();

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";
  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  const saveCompanyAndRedirect = useCallback((company) => {
    const compId = company.CompID || company.CompId || company.id;
    const compName = company.Name || company.name;
    const logoPath = company.LogoPath || null;
    const companyCode = company.CompanyCode || company.companyCode || "";
    const companyColourCode = company.CompanyColourCode || company.companyColourCode || "";

    const selected = {
      id: compId,
      name: compName,
      LogoPath: logoPath,
      CompanyCode: companyCode,
      CompanyColourCode: companyColourCode,
    };

    setSelectedCompany(selected);

    localStorage.setItem("companyId", compId);
    localStorage.setItem("companyName", compName);
    localStorage.setItem("selectedCompany", JSON.stringify(selected));

    window.dispatchEvent(new Event("companyChanged"));

    showToast.success(`Company selected — ${compName}`);

    setTimeout(() => {
      navigate("/home");
    }, 400);
  }, [navigate, setSelectedCompany]);

  const fetchCompanies = useCallback(async (userId) => {
    setLoading(true);

    try {
      if (!userId) {
        showToast.error("Session expired. Please login again.");
        navigate("/");
        return;
      }

      try {
        const nodeApiUrl = `${BASE_API_URL}/company/by-master/${userId}`;
        const res = await fetch(nodeApiUrl);
        if (res.ok) {
          const data = await res.json();
          const list = data.data || data.Response || [];
          if (Array.isArray(list) && list.length > 0) {
            setCompanies(list);
            setNoCompany(false);
            localStorage.setItem("companyList", JSON.stringify(list));

            if (list.length === 1) {
              saveCompanyAndRedirect(list[0]);
              return;
            }
            setLoading(false);
            return;
          }
        }
      } catch (nodeErr) {
        console.warn("Node backend company fetch failed, falling back to REST API:", nodeErr);
      }

      if (BASE_REST_API_URL) {
        const postData = new URLSearchParams();
        postData.append("SecurityKey", SECURITY_KEY);
        postData.append("TableName", "company");
        postData.append("WhereCondition", `MasterId=${userId}`);
        postData.append("*", "*");

        const apiUrl = `${BASE_REST_API_URL.replace(/\/+$/, "")}/SelectData.aspx`;

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: postData.toString(),
        });

        const text = await response.text();
        const cleanText = text.replace(/^\uFEFF/, "").trim();
        const match = cleanText.match(/\{[\s\S]*\}/);

        if (match) {
          const json = JSON.parse(match[0]);
          if (json.Response && json.Response.length > 0) {
            setCompanies(json.Response);
            setNoCompany(false);
            localStorage.setItem("companyList", JSON.stringify(json.Response));

            if (json.Response.length === 1) {
              saveCompanyAndRedirect(json.Response[0]);
              return;
            }
            setLoading(false);
            return;
          }
        }
      }

      const saved = localStorage.getItem("companyList");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCompanies(parsed);
          setNoCompany(false);
          if (parsed.length === 1) {
            saveCompanyAndRedirect(parsed[0]);
            return;
          }
          setLoading(false);
          return;
        }
      }

      setCompanies([]);
      setNoCompany(true);
    } catch (error) {
      console.error("Fetch company error:", error);
      const saved = localStorage.getItem("companyList");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.length > 0) {
            setCompanies(parsed);
            setNoCompany(false);
            setLoading(false);
            return;
          }
        } catch {}
      }
      setNoCompany(true);
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, BASE_REST_API_URL, saveCompanyAndRedirect, navigate]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      navigate("/");
      return;
    }

    const saved = localStorage.getItem("companyList");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === 1) {
          saveCompanyAndRedirect(parsed[0]);
          return;
        }
      } catch {}
    }

    fetchCompanies(userId);
  }, [fetchCompanies, navigate, saveCompanyAndRedirect]);

  const handleContinue = () => {
    if (!selectedId) {
      showToast.warn("Please select a company.");
      return;
    }

    const company = companies.find(
      (c) => String(c.CompID || c.CompId || c.id) === String(selectedId)
    );

    if (!company) {
      showToast.error("Company not found!");
      return;
    }

    saveCompanyAndRedirect(company);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {loading ? (
          <CompanySelectSkeleton />
        ) : noCompany ? (
          <>
            <h2 style={styles.title}>No Company Found</h2>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              You don't have any company yet.
            </p>

            <button
              style={styles.button}
              onClick={() => navigate("/compney")}
            >
              Add Company
            </button>
          </>
        ) : (
          <>
            <h2 style={styles.title}>Select Your Company</h2>

            <select
              style={styles.dropdown}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">Select Company</option>

              {companies.map((company) => {
                const id = company.CompID || company.CompId || company.id;
                const name = company.Name || company.name;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>

            <button style={styles.button} onClick={handleContinue}>
              Continue
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    background: "#f5f5f5",
  },
  card: {
    background: "#fff",
    padding: "40px",
    borderRadius: "10px",
    boxShadow: "0 0 20px rgba(0,0,0,0.1)",
    textAlign: "center",
    minWidth: "340px",
    maxWidth: "420px",
    width: "90%",
  },
  title: {
    marginBottom: "20px",
    color: "#333",
    fontSize: "1.3rem",
    fontWeight: "600",
  },
  dropdown: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "15px",
    marginBottom: "20px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    outline: "none",
  },
  button: {
    width: "100%",
    padding: "12px",
    background: "#0d9488",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "500",
    transition: "background 0.2s",
  },
};

export default SelectCompany;
